import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { ListShell } from '../components/ListShell';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import {
  ConfigForm,
  type PerformanceConfigResponse,
} from '../components/performance';
import { formatDate } from '../lib/format';

/**
 * System-level dedicated surface for editing the TMA performance
 * scoring formula (weights, thresholds, signal-include flags).
 *
 * The same form is also embedded in the HR performance page's Config
 * tab — we expose it here so super-admins can find it under the
 * System group in the sidebar without first navigating into HR. The
 * form, query keys, and PATCH endpoint are shared with the HR tab,
 * so changes saved from either surface invalidate the other.
 *
 * Role-guarded: super_admin only. Other roles get bounced to their
 * landing page. The server enforces real RBAC; this guard is purely
 * UX so the nav entry doesn't show non-admins a 403.
 */
export const Route = createFileRoute('/_authed/system/performance-config')({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.primaryRole !== 'super_admin') {
      throw redirect({ to: '/' });
    }
  },
  component: PerformanceConfigPage,
});

function PerformanceConfigPage() {
  const configQuery = useQuery({
    queryKey: ['performance', 'config'] as const,
    queryFn: ({ signal }) =>
      apiRequest<PerformanceConfigResponse>('/api/performance/config', {
        signal,
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  return (
    <ListShell
      title="Score settings"
      description="Tune the TMA scoring formula — variable weights, performance bands, and which signals are included. Saving creates a new config; today's snapshots will be recomputed at the next nightly cron, but yesterday's snapshots stay unchanged."
    >
      <GatedSection
        error={configQuery.error}
        title="Score settings"
        onRetry={() => configQuery.refetch()}
      >
        {configQuery.isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : !configQuery.data?.config ? (
          <EmptyState
            title="No active config yet"
            description="The default config will be seeded on the first read or first PATCH."
          />
        ) : (
          <div className="space-y-4">
            <header className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 py-3 text-xs text-[color:var(--color-text-muted)]">
              <span>
                Active since{' '}
                <span className="font-medium text-[color:var(--color-text)]">
                  {formatDate(configQuery.data.config.effectiveFrom, {
                    withTime: true,
                  })}
                </span>
              </span>
            </header>
            <ConfigForm config={configQuery.data.config} />
          </div>
        )}
      </GatedSection>
    </ListShell>
  );
}
